import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import clsx from 'clsx';
import { Button } from '../Button';
import { useT } from '../../i18n';
import { lockBodyScroll } from '../../lib/scrollLock';
import styles from './Drawer.module.css';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Plain string or rich node (e.g. EntityLink for drill-in titles). */
  title: ReactNode;
  description?: ReactNode;
  /** Width preset. Defaults to 'md' (440px). */
  size?: 'sm' | 'md' | 'lg';
  /** Footer slot — typically save / cancel buttons. */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Right-side slide-over drawer. Preferred over Modal for record details and
 * multi-field forms (per §4 of ui-system.md).
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  children,
}: DrawerProps) {
  const { t } = useT();
  useEffect(() => {
    if (!open) return;
    const unlock = lockBodyScroll();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      unlock();
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <aside
        className={clsx(styles.panel, styles[`size_${size}`])}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        <header className={styles.header}>
          <div className={styles.headerText}>
            <h2 id="drawer-title" className={styles.title}>{title}</h2>
            {description && <p className={styles.description}>{description}</p>}
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onClose}
            aria-label={t('components.drawer.close')}
            leftIcon={<X size={16} />}
          />
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </aside>
    </div>,
    document.body,
  );
}
